/**
 * ReportOrgActions — AI-generated action items per organisation.
 * Last section of the report: "Toimenpiteet organisaatioille"
 */
import { useState, useMemo } from 'react';
import styled from '@emotion/styled';
import { ChevronDown, ChevronUp, Crown, Users, Building2 } from 'lucide-react';
import { Section, SectionTitle, Paragraph } from './reportStyles';
import { useTranslation } from '../../hooks/useTranslation.jsx';
import EditableText from './EditableText';

const OrgGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-top: 24px;
`;

const OrgCard = styled.div`
  background: #fff;
  border: 1px solid #e5e7eb;
  border-left: 4px solid #367ABA;
  border-radius: 0;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  @media print { page-break-inside: avoid; box-shadow: none; }
`;

const OrgHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  cursor: pointer;
  user-select: none;
  &:hover { background: #fafafa; }
  @media print { &:hover { background: transparent; } }
`;

const OrgName = styled.h4`
  font-size: 16px;
  font-weight: 600;
  color: #000;
  margin: 0;
  flex: 1;
`;

const ActionCount = styled.span`
  font-size: 12px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 0;
  background: #f5f5f5;
  color: #367ABA;
  flex-shrink: 0;
`;

const ToggleIcon = styled.span`
  color: #9ca3af;
  flex-shrink: 0;
  @media print { display: none; }
`;

const OrgBody = styled.div`
  padding: 0 20px 16px;
  border-top: 1px solid #f3f4f6;
`;

const ActionList = styled.ol`
  margin: 12px 0 0;
  padding: 0 0 0 20px;
  list-style: none;
  counter-reset: action-counter;
`;

const ActionItem = styled.li`
  counter-increment: action-counter;
  position: relative;
  padding: 12px 0 12px 0;
  border-bottom: 1px solid #f9fafb;
  &:last-child { border-bottom: none; }
`;

const ActionRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
`;

const UrgencyDot = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
  margin-top: 5px;
  background: ${p =>
    p.$urgency === 'now' ? '#fa6129' :
    p.$urgency === 'soon' ? '#fa6129' : '#00613d'};
  @media print { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
`;

const ActionText = styled.div`
  font-size: 14px;
  line-height: 1.6;
  color: #333;
  flex: 1;
`;

const ActionMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-top: 6px;
  font-size: 12px;
  color: #6b7280;
`;

const MetaBadge = styled.span`
  padding: 1px 8px;
  border-radius: 2px;
  background: ${p => p.$bg || '#f3f4f6'};
  color: ${p => p.$color || '#6b7280'};
  font-weight: 500;
  @media print { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
`;

const SolutionLink = styled.span`
  font-style: italic;
  color: #6b7280;
`;

const Reasoning = styled.div`
  font-size: 13px;
  color: #9ca3af;
  margin-top: 4px;
  font-style: italic;
`;

const ExpandAllButton = styled.button`
  background: none;
  border: none;
  color: #367ABA;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  padding: 4px 0;
  margin-top: 8px;
  &:hover { color: #2c5e91; }
  @media print { display: none; }
`;

// ─── Overview ────────────────────────────────────────────────────────────────

const OverviewGrid = styled.div`
  margin: 20px 0;
`;

const OverviewCard = styled.div`
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 0;
  padding: 18px 20px;
`;

const OverviewLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 12px;
`;

const LeadRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 0;
  font-size: 14px;
  color: #000;
`;

const LeadBar = styled.div`
  flex: 1;
  height: 8px;
  background: #f3f4f6;
  border-radius: 4px;
  overflow: hidden;
`;

const LeadBarFill = styled.div`
  height: 100%;
  border-radius: 4px;
  background: ${p => p.$color || '#367ABA'};
  width: ${p => p.$width}%;
`;

const LeadName = styled.span`
  font-weight: 500;
  min-width: 180px;
  flex-shrink: 0;
  font-size: 13px;
`;

const LeadCount = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  min-width: 20px;
  text-align: right;
  flex-shrink: 0;
`;

const RoleBadge = styled.span`
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  padding: 1px 6px;
  border-radius: 4px;
  color: ${p => p.$lead ? '#2c5e91' : '#6b7280'};
  background: ${p => p.$lead ? '#f5f5f5' : '#f3f4f6'};
`;

// Urgency labels will be translated in component via t() hook

export default function ReportOrgActions({ actions = [], solutions = [], printMode = false }) {
  const { t } = useTranslation();
  // Group by org
  const grouped = {};
  for (const a of actions) {
    const key = a.organisation_id;
    if (!grouped[key]) grouped[key] = { name: a.org_name, items: [] };
    grouped[key].items.push(a);
  }
  const orgEntries = Object.entries(grouped).sort(([, a], [, b]) => a.name.localeCompare(b.name));

  const [expandedIds, setExpandedIds] = useState(() =>
    printMode ? new Set(orgEntries.map(([id]) => id)) : new Set()
  );
  const [showOrgs, setShowOrgs] = useState(printMode);

  // Top lead organisations from implementation plans
  const leadOrgs = useMemo(() => {
    const counts = {};
    solutions.forEach(s => {
      const leads = s.ai_analysis?.implementationPlan?.leadOrganisations || [];
      leads.forEach(l => {
        const key = l.orgName;
        if (!counts[key]) counts[key] = { lead: 0, support: 0, advisor: 0, total: 0 };
        counts[key][l.role] = (counts[key][l.role] || 0) + 1;
        counts[key].total++;
      });
    });
    return Object.entries(counts)
      .sort((a, b) => b[1].total - a[1].total)
      .slice(0, 8);
  }, [solutions]);

  if (!orgEntries.length) return null;

  const URGENCY_LABELS = {
    now: t('report.orgActions.urgencyNow'),
    soon: t('report.orgActions.urgencySoon'),
    next: t('report.orgActions.urgencyLater'),
  };
  const EFFORT_CONFIG = {
    small: { label: t('report.orgActions.effortSmall'), bg: '#ecfdf5', color: '#00613d' },
    medium: { label: t('report.orgActions.effortMedium'), bg: '#fffbeb', color: '#fa6129' },
    large: { label: t('report.orgActions.effortLarge'), bg: 'rgba(232, 17, 75, 0.08)', color: '#fa6129' },
  };

  // Solution title lookup
  const solutionTitles = {};
  solutions.forEach(s => {
    solutionTitles[s.id] = s.metadata?.ai_title || s.title?.split(/[.!?\n]/)[0] || s.title;
  });

  const allExpanded = expandedIds.size === orgEntries.length;

  // Summary stats
  const nowCount = actions.filter(a => a.urgency === 'now').length;
  const soonCount = actions.filter(a => a.urgency === 'soon').length;
  const nextCount = actions.filter(a => a.urgency === 'next').length;



  const toggle = (id) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    setExpandedIds(allExpanded ? new Set() : new Set(orgEntries.map(([id]) => id)));
  };

  return (
    <Section id="org-actions">
      <SectionTitle>{t('report.orgActions.title')}</SectionTitle>
      <Paragraph>
        <EditableText
          block
          fieldKey="orgActions.intro"
          fallback={t('report.orgActions.intro', {
            orgs: orgEntries.length,
            actions: actions.length,
            now: nowCount,
            soon: soonCount,
            next: nextCount,
          })}
        />
      </Paragraph>

      {/* Overview: top lead organisations from solution plans */}
      {leadOrgs.length > 0 && (
        <OverviewGrid>
          <OverviewCard>
            <OverviewLabel><Crown size={14} /> {t('report.orgActions.overview')}</OverviewLabel>
            {leadOrgs.map(([name, counts]) => {
              const maxCount = leadOrgs[0][1].total;
              return (
                <LeadRow key={name}>
                  <LeadName>{name}</LeadName>
                  <LeadBar>
                    <LeadBarFill $width={(counts.total / maxCount) * 100} $color={counts.lead > 0 ? '#367ABA' : '#94a3b8'} />
                  </LeadBar>
                  <LeadCount>{counts.total}</LeadCount>
                  {counts.lead > 0 && <RoleBadge $lead>lead {counts.lead}</RoleBadge>}
                </LeadRow>
              );
            })}
          </OverviewCard>
        </OverviewGrid>
      )}

      <ExpandAllButton onClick={() => setShowOrgs(!showOrgs)}>
        {showOrgs ? (
          <>{t('report.orgActions.expandLess')} <ChevronUp size={14} /></>
        ) : (
          <>{t('report.orgActions.expandMore', { count: orgEntries.length })} <ChevronDown size={14} /></>
        )}
      </ExpandAllButton>

      {showOrgs && (
        <>
          <ExpandAllButton onClick={toggleAll} style={{ marginTop: 0 }}>
            {allExpanded ? (
              <>{t('report.orgActions.collapseAll')} <ChevronUp size={14} /></>
            ) : (
              <>{t('report.orgActions.expandAll')} {t('report.orgActions.organisationsCount', { count: orgEntries.length })} <ChevronDown size={14} /></>
            )}
          </ExpandAllButton>

          <OrgGrid>
            {orgEntries.map(([orgId, { name, items }]) => {
              const isOpen = expandedIds.has(orgId);
              return (
                <OrgCard key={orgId}>
                  <OrgHeader onClick={() => toggle(orgId)}>
                    <OrgName>{name}</OrgName>
                    <ActionCount>{items.length} toimenpidettä</ActionCount>
                    <ToggleIcon>
                      {isOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                    </ToggleIcon>
                  </OrgHeader>

                  {isOpen && (
                    <OrgBody>
                      <ActionList>
                        {items.map((item, i) => {
                          const effort = EFFORT_CONFIG[item.effort] || EFFORT_CONFIG.small;
                          return (
                            <ActionItem key={item.id || i}>
                              <ActionRow>
                                <UrgencyDot $urgency={item.urgency} title={URGENCY_LABELS[item.urgency]} />
                                <ActionText>
                                  {item.id
                                    ? <EditableText fieldKey={`orgAction.${item.id}.action_text`} fallback={item.action_text} label="Toimenpide" />
                                    : item.action_text}
                                  <ActionMeta>
                                    <MetaBadge
                                      $bg={item.urgency === 'now' ? 'rgba(232, 17, 75, 0.08)' : item.urgency === 'soon' ? '#fffbeb' : '#ecfdf5'}
                                      $color={item.urgency === 'now' ? '#fa6129' : item.urgency === 'soon' ? '#fa6129' : '#00613d'}
                                    >
                                      {URGENCY_LABELS[item.urgency]}
                                    </MetaBadge>
                                    <MetaBadge $bg={effort.bg} $color={effort.color}>
                                      {effort.label}
                                    </MetaBadge>
                                    {item.solution_id && solutionTitles[item.solution_id] && (
                                      <SolutionLink>→ {solutionTitles[item.solution_id]}</SolutionLink>
                                    )}
                                  </ActionMeta>
                                  {item.reasoning && (
                                    item.id
                                      ? <Reasoning><EditableText fieldKey={`orgAction.${item.id}.reasoning`} fallback={item.reasoning} label="Perustelu" /></Reasoning>
                                      : <Reasoning>{item.reasoning}</Reasoning>
                                  )}
                                </ActionText>
                              </ActionRow>
                            </ActionItem>
                          );
                        })}
                      </ActionList>
                    </OrgBody>
                  )}
                </OrgCard>
              );
            })}
          </OrgGrid>
        </>
      )}
    </Section>
  );
}
